import { zodResolver } from "@hookform/resolvers/zod";
import useToast from "@repo/core/components/core/Toast";
import { proposalPhoneFormDataSchema } from "@repo/core/helpers/zod.schemas";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { apiInstance } from "@repo/core/services/instances/api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { PhoneFormDataSchemaType } from "@repo/core/stores/proposalForm/types";
import { useForm } from "react-hook-form";
import { usePreProposal } from "../../hooks/usePreProposal";
import { cancelPreProposalAlterarSyncDebounce } from "../../services/preProposalAlterarSyncDebounce.service";
import { PROPOSTA_SITUACAO } from "@repo/core/services/entities/Proposals";
import { ROUTES } from "@repo/core/routes";
import { CommonResource } from "@repo/core/services/resources/common";
import { useRef, useEffect, useCallback } from "react";
import { isValidCPF } from "@repo/core/helpers/zod.helpers";
import { useDebounceCallback } from "usehooks-ts";
import { getCleanCPF } from "@repo/core/utils/string.utils";
import {
  splitPhoneNumber,
  buildProposalFormDataFromPhone,
  mapToCreateProposalInput,
  mapToUpdatePreProposalInput
} from "./mappers";
import {
  PHONE_ALREADY_REGISTERED_MESSAGE,
  getPhoneErrorFromApiResponse,
  validatePhoneAgainstCpf
} from "@repo/core/services/validatePhoneAgainstCpf.service";

const proposalsResource = new ProposalsResource(apiInstance);
const commonResource = new CommonResource(apiInstance);

export const PhoneNumberController = () => {
  const { push, replace } = useNavigation();
  const { error } = useToast();

  const { setProposalData, proposalData } = usePreProposal();

  const {
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { isSubmitting },
    handleSubmit
  } = useForm<PhoneFormDataSchemaType>({
    resolver: zodResolver(proposalPhoneFormDataSchema)
  });

  const cellPhoneValue = watch("cellPhone");
  const previousPhoneRef = useRef<string | undefined>();

  const validatePhoneNumber = async (
    phoneDDD: string,
    phone: string,
    cleanCPF: string
  ) => validatePhoneAgainstCpf(proposalsResource, phoneDDD, phone, cleanCPF);

  const handlePhoneChange = useCallback(
    async (phone: string) => {
      if (!phone || phone.length !== 11) {
        return;
      }

      const firstDigitAfterDDD = phone[2];
      if (firstDigitAfterDDD !== "8" && firstDigitAfterDDD !== "9") {
        setError("cellPhone", {
          type: "manual",
          message: "O número do celular deve iniciar com 8 ou 9."
        });
        return;
      }

      const { phoneDDD, phone: phoneNumber } = splitPhoneNumber(phone);
      const cleanCPF = proposalData?.strCliente
        ? getCleanCPF(proposalData.strCliente)
        : "";

      if (!cleanCPF || !isValidCPF(cleanCPF)) {
        return;
      }

      try {
        const result = await validatePhoneNumber(
          phoneDDD,
          phoneNumber,
          cleanCPF
        );
        if (result.isValid) {
          clearErrors("cellPhone");
        } else {
          setError("cellPhone", {
            type: "manual",
            message: result.errorMessage || PHONE_ALREADY_REGISTERED_MESSAGE
          });
        }
      } catch (err) {
        console.error("Error validating phone:", err);
        const apiMessage = getPhoneErrorFromApiResponse(err);
        setError("cellPhone", {
          type: "manual",
          message: apiMessage || "Erro ao validar o telefone. Tente novamente."
        });
      }
    },
    [proposalData?.strCliente, setError, clearErrors]
  );

  const debouncedHandlePhoneChange = useDebounceCallback(
    handlePhoneChange,
    500
  );
  const debouncedHandlePhoneChangeRef = useRef(debouncedHandlePhoneChange);
  debouncedHandlePhoneChangeRef.current = debouncedHandlePhoneChange;

  useEffect(() => {
    if (!proposalData?.strCliente || !proposalData?.strNome) {
      replace(ROUTES.preProposalsInfoSecurity);
      return;
    }

    if (proposalData?.strEndDdd1 && proposalData?.strEndFone1) {
      const fullPhone = `${proposalData.strEndDdd1}${proposalData.strEndFone1}`;
      if (fullPhone.length === 11) {
        setValue("cellPhone", fullPhone);
        previousPhoneRef.current = fullPhone;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentPhone = cellPhoneValue;
    const previousPhone = previousPhoneRef.current;

    if (currentPhone !== previousPhone) {
      clearErrors("cellPhone");
    }

    if (
      currentPhone &&
      currentPhone.length === 11 &&
      currentPhone !== previousPhone
    ) {
      const cleanCPF = proposalData?.strCliente
        ? getCleanCPF(proposalData.strCliente)
        : "";

      if (cleanCPF && isValidCPF(cleanCPF)) {
        debouncedHandlePhoneChangeRef.current(currentPhone);
      }
    }

    previousPhoneRef.current = currentPhone;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cellPhoneValue, proposalData?.strCliente]);

  const createOrGetProposal = async (
    formData: {
      phoneDDD: string;
      phone: string;
      cleanCPF: string;
      fullName: string;
    },
    dataHasChanged: boolean
  ): Promise<number> => {
    const proposalId = proposalData?.lngProposta;

    if (!proposalId) {
      const proposalInput = mapToCreateProposalInput(formData, proposalData);

      const { data: proposalResponse } =
        await proposalsResource.createProposal(proposalInput);

      return proposalResponse.lngProposta;
    }

    if (dataHasChanged) {
      const updateInput = mapToUpdatePreProposalInput(
        formData,
        Number(proposalId),
        proposalData
      );

      cancelPreProposalAlterarSyncDebounce();
      await proposalsResource.editProposal(updateInput);
    }

    return Number(proposalId);
  };

  const generateTokenAndNavigate = async (formData: {
    phoneDDD: string;
    phone: string;
    cleanCPF: string;
    fullName: string;
  }) => {
    const { data: generateTokenResponse } = await commonResource.generateToken({
      strDDD: formData.phoneDDD,
      strTelefone: formData.phone,
      strCliente: formData.cleanCPF,
      strNome: formData.fullName,
      strProduto: proposalData?.strProduto || ""
    });

    if (generateTokenResponse.blnSms) {
      push(ROUTES.preProposalsConfirmToken);
    } else {
      setError("cellPhone", {
        type: "manual",
        message: "Erro ao gerar o token, tente novamente."
      });
    }
  };

  const handleOnSubmitForm = handleSubmit(
    async (data: PhoneFormDataSchemaType) => {
      if (!proposalData?.strCliente || !proposalData?.strNome) {
        error("Dados incompletos. Retorne à tela anterior.");
        replace(ROUTES.preProposalsInfoSecurity);
        return;
      }

      try {
        const formData = buildProposalFormDataFromPhone(
          data.cellPhone,
          proposalData
        );

        const validationResult = await validatePhoneNumber(
          formData.phoneDDD,
          formData.phone,
          formData.cleanCPF
        );

        if (!validationResult.isValid) {
          setError("cellPhone", {
            type: "manual",
            message:
              validationResult.errorMessage || PHONE_ALREADY_REGISTERED_MESSAGE
          });
          return;
        }

        const savedPhone =
          proposalData.strEndDdd1 && proposalData.strEndFone1
            ? `${proposalData.strEndDdd1}${proposalData.strEndFone1}`
            : undefined;
        const currentPhone = `${formData.phoneDDD}${formData.phone}`;
        const phoneHasChanged = savedPhone ? currentPhone !== savedPhone : true;
        const contextHasChanged = !!proposalData?.needsContextUpdate;
        const dataHasChanged = phoneHasChanged || contextHasChanged;

        // Whether createOrGetProposal will call editProposal internally
        const hadExistingProposal = !!proposalData?.lngProposta;
        const willCallAlterar = hadExistingProposal && dataHasChanged;

        const proposalId = await createOrGetProposal(formData, dataHasChanged);

        // Skip debounced Alterar when createOrGetProposal already persisted, or when
        // phone/context did not change (no API sync needed).
        setProposalData(
          {
            lngProposta: proposalId,
            strEndDdd1: formData.phoneDDD,
            strEndFone1: formData.phone,
            strNome: formData.fullName,
            strCliente: formData.cleanCPF,
            strSituacao: PROPOSTA_SITUACAO.DATA_CONFERENCE,
            needsContextUpdate: false,
            ...(phoneHasChanged && {
              shouldForceNewBiometry: true,
              biometryReuseProcessId: undefined,
              strIdProcessoUnico: undefined
            })
          },
          { skipAlterarSync: willCallAlterar || !dataHasChanged }
        );

        await generateTokenAndNavigate(formData);
      } catch (err) {
        console.error("Error submitting phone:", err);
        const apiMessage = getPhoneErrorFromApiResponse(err);
        setError("cellPhone", {
          type: "manual",
          message:
            apiMessage || "Erro ao validar as informações, tente novamente."
        });
      }
    }
  );

  const handleBack = () => {
    replace(ROUTES.preProposalsInfoSecurity);
  };

  return {
    control,
    isSubmitting,
    handleOnSubmitForm,
    back: handleBack
  };
};
